import { describeKnowledgeApiError } from 'utils/knowledgeErrors';
import { css } from '@emotion/css';
import { Alert, Button, Space, Typography, message } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import type { KnowledgeId, ProductKnowledgeScopeSnapshot } from '../../model/types';
import { selectedMembers } from '../../model/selection';
import { useQueryClient } from '@tanstack/react-query';
import {
  addProductKnowledgeScopeMember,
  removeProductKnowledgeScopeMember,
  searchProductKnowledgeScopeProducts,
  getProductKnowledgeDetail,
} from '../../api/productKnowledge';
import { ProductPickerDrawer, type ProductPickerSelection } from '../ProductPickerDrawer';

const editorClassName = css`
  display: flex;
  flex-direction: column;
  gap: 8px;

  .document-scope-mode-help {
    color: var(--fett-text-tertiary, #bfbfbf);
    cursor: help;
  }
`;

export interface DocumentScopeEditorProps {
  knowledgeType: 'SPU_DETAIL_KNOWLEDGE' | 'DOCUMENT_KNOWLEDGE' | 'SUPPLEMENTAL_KNOWLEDGE';
  knowledgeId: KnowledgeId;
  value: ProductKnowledgeScopeSnapshot;
  onChange: (scope: ProductKnowledgeScopeSnapshot) => void;
  disabled?: boolean;
  /** 客服接待知识的 scope.expand 是只读投影，不展示关联模式开关 */
  showScopeMode?: boolean;
}

/**
 * 知识关联维护（对齐 perseus KnowledgeScopeProductPicker）：
 * 计数 + 查看关联/扩展更多两个入口，抽屉体复用共享 ProductPickerDrawer
 */
export function DocumentScopeEditor({
  knowledgeType,
  knowledgeId,
  disabled = false,
}: DocumentScopeEditorProps) {
  const { t } = useTranslate();
  const queryClient = useQueryClient();
  const [drawerMode, setDrawerMode] = useState<'ASSOCIATED' | 'EXPANDABLE'>();
  // 嵌套「添加关联商品」子抽屉（对齐 perseus onAdd/nested ProductSelectionContext）：
  // 在已关联抽屉内继续添加，确认后不关闭任何一层、清空子层勾选并刷新父层
  const [nestedAdd, setNestedAdd] = useState(false);
  const [nestedSaving, setNestedSaving] = useState(false);
  const [revision, setRevision] = useState(0);
  const [counts, setCounts] = useState<{ spuCount: number; skuCount: number }>();
  const [summaryError, setSummaryError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string>();
  const [savedNeedsRefresh, setSavedNeedsRefresh] = useState(false);
  const identityRef = useRef(`${knowledgeType}:${knowledgeId}`);
  identityRef.current = `${knowledgeType}:${knowledgeId}`;

  const refreshSummary = useCallback(async () => {
    const detail = await getProductKnowledgeDetail(knowledgeType, knowledgeId);
    if (identityRef.current !== `${knowledgeType}:${knowledgeId}`) return;
    const count = detail.associatedCount;
    if (
      !count ||
      !Number.isInteger(count.spuCount) ||
      !Number.isInteger(count.skuCount) ||
      count.spuCount < 0 ||
      count.skuCount < 0
    ) {
      throw new Error('Invalid associatedCount');
    }
    setCounts(count);
    setSummaryError(false);
  }, [knowledgeType, knowledgeId]);

  const refreshAfterMutation = async () => {
    // Do not refetch the outer editor: its form must retain the unsaved answer.
    await Promise.all([
      refreshSummary().catch((error) => {
        setSummaryError(true);
        throw error;
      }),
      queryClient.invalidateQueries(
        {
          queryKey: ['productKnowledge'],
          predicate: (query) => !query.queryKey.includes('editor'),
        },
        { throwOnError: true },
      ),
    ]);
  };

  useEffect(() => {
    setCounts(undefined);
    setSummaryError(false);
    void refreshSummary().catch(() => {
      if (identityRef.current === `${knowledgeType}:${knowledgeId}`) setSummaryError(true);
    });
  }, [knowledgeType, knowledgeId, refreshSummary]);

  const addMembers = async (selection: ProductPickerSelection) => {
    const members = selectedMembers(selection.products, selection.selectedKeys);
    if (!members.length) return;
    await addProductKnowledgeScopeMember({ knowledgeType, knowledgeId, members });
  };

  const saveSelection = async ({ products, selectedKeys }: ProductPickerSelection) => {
    if (!drawerMode || saving) return;
    setSaving(true);
    if (savedNeedsRefresh) {
      try {
        await refreshAfterMutation();
        setSavedNeedsRefresh(false);
        setNotice(undefined);
      } catch {
        setNotice('productKnowledge_mapping_saved_refresh_failed');
      } finally {
        setSaving(false);
      }
      return;
    }
    try {
      const members = selectedMembers(products, selectedKeys);
      if (!members.length) return;
      const input = { knowledgeType, knowledgeId, members };
      if (drawerMode === 'ASSOCIATED') await removeProductKnowledgeScopeMember(input);
      else await addProductKnowledgeScopeMember(input);
      setSavedNeedsRefresh(true);
      try {
        await refreshAfterMutation();
        setSavedNeedsRefresh(false);
        setNotice(undefined);
        message.success(
          t(
            drawerMode === 'ASSOCIATED'
              ? 'productKnowledge_scope_editor_remove_success'
              : 'productKnowledge_scope_editor_add_success',
          ),
        );
      } catch {
        setNotice('productKnowledge_mapping_saved_refresh_failed');
      }
    } catch (error) {
      const response = error as {
        code?: number;
        response?: { status?: number; data?: { code?: number } };
      };
      const code = Number(response.response?.data?.code ?? response.code);
      // 对齐 perseus：业务冲突码 4309 与 HTTP 409 均视为「关联已变化」
      if (response.response?.status === 409 || code === 4209 || code === 4309) {
        setNotice('productKnowledge_mapping_conflict');
        await refreshAfterMutation().catch(() => undefined);
      } else setNotice('productKnowledge_mapping_save_failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={editorClassName}>
      <Typography.Text type="secondary">{t('productKnowledge_mapping_immediate')}</Typography.Text>
      {summaryError ? (
        <Alert
          type="error"
          message={t('productKnowledge_scope_editor_search_failed')}
          action={
            <Button onClick={() => void refreshSummary().catch(() => setSummaryError(true))}>
              {t('productKnowledge_common_retry')}
            </Button>
          }
        />
      ) : null}
      <Space>
        <Typography.Text strong>
          {counts
            ? t('productKnowledge_scope_editor_associated_count', {
                spu: counts.spuCount,
                sku: counts.skuCount,
              })
            : '—'}
        </Typography.Text>
        <Button disabled={disabled || !knowledgeId} onClick={() => setDrawerMode('ASSOCIATED')}>
          {t('productKnowledge_scope_editor_view_associated')}
        </Button>
        <Button disabled={disabled || !knowledgeId} onClick={() => setDrawerMode('EXPANDABLE')}>
          {t('productKnowledge_scope_editor_expand_more')}
        </Button>
      </Space>
      {drawerMode ? (
        <ProductPickerDrawer
          visible
          key={`${drawerMode}:${knowledgeType}:${knowledgeId}`}
          title={t(
            drawerMode === 'ASSOCIATED'
              ? 'productKnowledge_scope_editor_current_associated'
              : 'productKnowledge_scope_editor_expand_more',
          )}
          confirmLabel={t(
            savedNeedsRefresh
              ? 'productKnowledge_common_retry'
              : drawerMode === 'ASSOCIATED'
              ? 'productKnowledge_scope_editor_delete_selected'
              : 'productKnowledge_scope_editor_add_selected',
          )}
          confirmLoading={saving}
          confirmDisabled={savedNeedsRefresh ? false : undefined}
          frozen={saving || savedNeedsRefresh || disabled}
          notice={notice ? <Alert type="warning" message={t(notice)} /> : null}
          onClose={() => {
            setDrawerMode(undefined);
            setNestedAdd(false);
          }}
          onConfirm={saveSelection}
          search={({ pageNo, pageSize, itemSearch }) =>
            searchProductKnowledgeScopeProducts({
              knowledgeType,
              knowledgeId,
              mode: drawerMode,
              itemSearch,
              pageNo,
              pageSize,
            })
          }
          keyMode="sku"
          closeDisabled={nestedAdd}
          reloadKey={revision}
          headerExtra={
            drawerMode === 'ASSOCIATED' ? (
              <Button size="small" disabled={saving || disabled} onClick={() => setNestedAdd(true)}>
                {t('productKnowledge_scope_editor_expand_more')}
              </Button>
            ) : undefined
          }
        />
      ) : null}
      {drawerMode === 'ASSOCIATED' && nestedAdd ? (
        <ProductPickerDrawer
          visible
          width={888}
          title={t('productKnowledge_scope_editor_expand_more')}
          confirmLabel={t('productKnowledge_scope_editor_add_selected')}
          confirmLoading={nestedSaving}
          frozen={nestedSaving || disabled}
          onClose={() => setNestedAdd(false)}
          onConfirm={async (selection) => {
            // 保存不关抽屉：确认后清空子层勾选（组件内），刷新父层列表与计数
            setNestedSaving(true);
            try {
              await addMembers(selection);
              setRevision((current) => current + 1);
              await refreshAfterMutation();
              message.success(t('productKnowledge_scope_editor_add_success'));
            } catch (error) {
              const response = error as {
                code?: number;
                response?: { status?: number; data?: { code?: number } };
              };
              const code = Number(response.response?.data?.code ?? response.code);
              // 对齐 perseus：业务冲突码 4309 与 HTTP 409 均视为「关联已变化」
              if (response.response?.status === 409 || code === 4209 || code === 4309) {
                setRevision((current) => current + 1);
                await refreshAfterMutation().catch(() => undefined);
              }
              message.error(
                describeKnowledgeApiError(
                  error,
                  undefined,
                  t('productKnowledge_mapping_save_failed'),
                ),
              );
            } finally {
              setNestedSaving(false);
            }
          }}
          search={({ pageNo, pageSize, itemSearch }) =>
            searchProductKnowledgeScopeProducts({
              knowledgeType,
              knowledgeId,
              mode: 'EXPANDABLE',
              itemSearch,
              pageNo,
              pageSize,
            })
          }
          keyMode="sku"
        />
      ) : null}
    </div>
  );
}
